import React, { useCallback, useRef } from "react";
import { Box } from "components/mui";
import { RichTextEditor, DEFAULT_PLUGINS } from "components/RichTextEditor";
import { uploadEmailImage } from "utils/canmail/uploadEmailImage";
import { ROUTE_BASE } from "constants/routerPath";
import VariableToolbar from "./VariableToolbar";

// Email needs a live preview on top of the shared editor's default plugins.
const EMAIL_PLUGINS = [...DEFAULT_PLUGINS, "preview"];

// Headline additions over the shared default toolbar: `code` (raw HTML source — the
// point of this editor), `table`, and `preview`. Everything else mirrors the standard
// toolbar so the email editor feels the same as the rest of the app.
const EMAIL_TOOLBAR =
  "aiformat | undo redo | bold italic underline strikethrough | " +
  "forecolor backcolor | alignleft aligncenter alignright alignjustify | " +
  "bullist numlist outdent indent | link image table | " +
  "removeformat | code preview fullscreen";

// Keep links absolute. By default the editor shortens links to our own site,
// and those links break once the email leaves the app.
const EMAIL_URL_OPTIONS = {
  relative_urls: false,
  remove_script_host: false,
  document_base_url: `${window.location.origin}${ROUTE_BASE}/`,
};

/**
 * The CANmail HTML email body editor.
 *
 * It is the shared TinyMCE `RichTextEditor` configured for email — public/absolute image
 * upload (`uploadEmailImage`), an always-light editing surface, and source/table/preview
 * — with the CANmail variable toolbar mounted above it.
 *
 * This is a drop-in replacement for the old ReactQuill `TextEditor`: same
 * `value`/`onChange` contract, so `EditorPane` and `useEmailBody` need no other changes.
 * `useEmailBody` is the seam — see its header for how the editors are swapped.
 */
function HtmlEditor({ value, onChange }) {
  const editorRef = useRef(null);

  const handleVariableInsert = useCallback((variable) => {
    const editor = editorRef.current;
    if (!editor) return;
    // TinyMCE keeps the last caret even after focus moved to the toolbar button that was
    // just clicked, so focusing and then inserting drops the variable where the user was
    // typing. With an untouched body the caret sits at the start, which is fine.
    editor.focus();
    editor.insertContent(variable);
  }, []);

  return (
    <Box>
      <VariableToolbar
        onVariableInsert={handleVariableInsert}
        content={value}
        showInsertButton={true}
        isDropEditor={false}
      />
      <RichTextEditor
        value={value}
        onChange={onChange}
        height={400}
        placeholder="Start typing your email... Use the variable toolbar above to insert personalized content."
        uploadHandler={uploadEmailImage}
        plugins={EMAIL_PLUGINS}
        toolbar={EMAIL_TOOLBAR}
        forceLightContent
        extraInit={EMAIL_URL_OPTIONS}
        onEditorInit={(editor) => {
          editorRef.current = editor;
        }}
      />
    </Box>
  );
}

export default HtmlEditor;
